import { useState, type FormEvent } from 'react';
import { ApiService } from '../services/apiService';
import { ViewMode } from '../contexts/ViewModeContext';
import { TrendQueryBuilder, formatTrendQuery, validateTrendQuery } from './TrendQueryBuilder';
import type { TrendQueryDefinition } from '../types/trendQuery';

interface CreateTrendCategoryDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  viewMode: ViewMode;
}

const initialDefinition: TrendQueryDefinition = {
  version: 1,
  root: { id: 'term-1', type: 'phrase', text: '' }
};

export const CreateTrendCategoryDialog = ({ isOpen, onClose, onSuccess, viewMode }: CreateTrendCategoryDialogProps) => {
  const [name, setName] = useState('');
  const [queryDefinition, setQueryDefinition] = useState<TrendQueryDefinition>(initialDefinition);
  const [fields, setFields] = useState({
    factsEmbedding: false,
    causeOfActionEmbedding: true,
    reliefEmbedding: false
  });
  const [threshold, setThreshold] = useState(0.7);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const apiService = ApiService.getInstance();
  const isAdvanced = viewMode === 'admin';

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    const validationMessage = validateTrendQuery(queryDefinition);
    if (validationMessage) {
      setError(validationMessage);
      return;
    }

    if (isAdvanced && !name.trim()) {
      setError('Name is required');
      return;
    }

    const selectedFields = isAdvanced
      ? Object.entries(fields).filter(([, selected]) => selected).map(([field]) => field)
      : ['causeOfActionEmbedding'];

    if (selectedFields.length === 0) {
      setError('Select at least one field');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      await apiService.createTrendCategory({
        name: isAdvanced ? name.trim() : formatTrendQuery(queryDefinition.root),
        query: formatTrendQuery(queryDefinition.root),
        queryDefinition,
        fields: selectedFields,
        threshold: isAdvanced ? threshold : 0.70
      });
      onSuccess();
      handleClose();
    } catch {
      setError('Failed to create topic');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setName('');
    setQueryDefinition(initialDefinition);
    setFields({ factsEmbedding: false, causeOfActionEmbedding: true, reliefEmbedding: false });
    setThreshold(0.7);
    setError('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div data-component="CreateTrendCategoryDialog" className="fixed inset-0 z-50 flex items-center justify-center">
      <div data-flow="dialog-backdrop" className="absolute inset-0 bg-black/40" onClick={handleClose} />
      <div data-flow="dialog-content" role="dialog" aria-modal="true" aria-labelledby="create-trend-category-title" className="relative max-h-[90vh] w-full max-w-2xl mx-4 overflow-y-auto rounded-lg bg-white p-6 shadow-xl">
        <h2 id="create-trend-category-title" className="mb-4 text-lg font-semibold text-gray-900">Create Trend Topic</h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          {isAdvanced && (
            <div>
              <label htmlFor="trend-category-name" className="mb-1 block text-sm font-medium text-gray-700">Name</label>
              <input
                id="trend-category-name"
                type="text"
                value={name}
                onChange={event => setName(event.target.value)}
                placeholder="e.g., Employment Discrimination"
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
              />
            </div>
          )}

          <TrendQueryBuilder value={queryDefinition} onChange={definition => { setQueryDefinition(definition); setError(''); }} />

          {isAdvanced && (
            <div data-flow="embedding-fields">
              <div className="mb-2 block text-sm font-medium text-gray-700">Fields</div>
              <div className="flex flex-wrap gap-4">
                {([
                  ['factsEmbedding', 'Facts'],
                  ['causeOfActionEmbedding', 'Cause'],
                  ['reliefEmbedding', 'Relief']
                ] as const).map(([field, label]) => (
                  <label key={field} className="flex items-center gap-1.5">
                    <input
                      type="checkbox"
                      checked={fields[field]}
                      onChange={event => setFields(previous => ({ ...previous, [field]: event.target.checked }))}
                      className="h-4 w-4 rounded border-gray-300 text-blue-600"
                    />
                    <span className="text-sm text-gray-600">{label}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {isAdvanced && (
            <div data-flow="threshold">
              <label htmlFor="trend-category-threshold" className="mb-1 block text-sm font-medium text-gray-700">Threshold: {threshold.toFixed(2)}</label>
              <input
                id="trend-category-threshold"
                type="range"
                min="0.5"
                max="0.95"
                step="0.01"
                value={threshold}
                onChange={event => setThreshold(parseFloat(event.target.value))}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-gray-400"><span>0.50</span><span>0.95</span></div>
            </div>
          )}

          {error && <p data-flow="error" role="alert" className="text-sm text-red-600">{error}</p>}

          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={handleClose} className="rounded-md bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200">Cancel</button>
            <button data-flow="submitting" type="submit" disabled={isSubmitting} className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50">{isSubmitting ? 'Creating...' : 'Create'}</button>
          </div>
        </form>
      </div>
    </div>
  );
};
